/* istanbul ignore file */

import { setup } from 'xstate5';

import { type Context, type Event, type Input } from './ota-update-state-machine.types';
import { Actions } from './setup/ota-update-state-machine.actions';
import { Actors } from './setup/ota-update-state-machine.actors';
import { Delays } from './setup/ota-update-state-machine.delays';
import { Guards } from './setup/ota-update-state-machine.guards';

// ─── Model ───────────────────────────────────────────────────────────────────

const model = setup({
  types: {
    input: {} as Input,
    context: {} as Context,
    events: {} as Event,
  },
  actions: Actions,
  actors: Actors,
  guards: Guards,
  delays: Delays,
});

// ─────────────────────────────────────────────────────────────────────────────

export const otaUpdateStateMachine = model.createMachine({
  context: ({ input }) => ({
    availableUpdate: undefined,
    options: input,
  }),

  // ─────────────────────────────────────────────────────────────

  initial: 'setup',
  states: {
    setup: {
      description:
        'The initial state where the machine determines the next step based on certain conditions',
      always: [
        {
          target: 'disabled',
          guard: 'checkIfUpdatesDisabled',
        },
        {
          target: '#checking-for-initial-update',
          guard: 'checkIfAutoUpdateEnabled',
        },
        {
          target: '#checking-for-regular-update',
        },
      ],
    },
    disabled: {
      id: 'disabled',
      description:
        'A state that notifies the application that updates are not supported or enabled for the current environment.',
      type: 'final',
    },
    checking: {
      initial: 'forInitialUpdate',
      description: 'An async state that checks for updates.',
      states: {
        forInitialUpdate: {
          id: 'checking-for-initial-update',
          description:
            'An async state that uses the associated service to check for an initial update. If no update is found, polling is triggered.',
          invoke: {
            id: 'checkForInitialUpdate',
            src: 'checkForInitialUpdate',
            input: ({ context }) => ({
              initialUpdateEventTimeout: context.options.initialUpdateEventTimeout,
            }),
            onDone: [
              {
                target: 'polling',
                guard: {
                  type: 'checkIfNoInitialUpdateAvailable',
                  params: ({ event }) => ({ result: event.output }),
                },
              },
              {
                target: '#storing-update-context',
                actions: [
                  {
                    type: 'onCheckForUpdateDone',
                    params: ({ event }) => ({ manifest: event.output?.manifest }),
                  },
                ],
              },
            ],
            onError: {
              target: 'forRegularUpdate',
              actions: ['onCheckForUpdateError'],
            },
          },
        },
        forRegularUpdate: {
          id: 'checking-for-regular-update',
          description:
            'An async state that uses the associated service to check for a regular update. If no update is found, polling is triggered.',
          invoke: {
            id: 'checkForUpdate',
            src: 'checkForUpdate',
            onDone: [
              {
                target: 'polling',
                guard: {
                  type: 'checkIfNoNewUpdateIsAvailable',
                  params: ({ event }) => ({ result: event.output }),
                },
              },
              {
                target: '#storing-update-context',
                actions: [
                  {
                    type: 'onCheckForUpdateDone',
                    params: ({ event }) => ({ manifest: event.output?.manifest }),
                  },
                ],
              },
            ],
            onError: {
              target: 'polling',
              actions: ['onCheckForUpdateError'],
            },
          },
        },
        polling: {
          id: 'polling',
          description:
            'A simple polling state that checks for a possible update based on the configurable time interval.',
          after: {
            POLLING_INTERVAL: 'forRegularUpdate',
          },
        },
      },
    },
    storingUpdateContext: {
      id: 'storing-update-context',
      description:
        'An async state that stores the available update context using the provided service.',
      invoke: {
        id: 'storeUpdateContext',
        src: 'storeUpdateContext',
        onDone: '#pending-update',
        onError: '#pending-update',
      },
    },
    pendingUpdate: {
      id: 'pending-update',
      description:
        "An intermediate state in which the app waits for the user's action to apply the update and restart the app or to continue using it (the update will be applied on the next app launch).",
      on: {
        RESTART: '#restarting',
        UPDATE_LATER: '#update-later-polling',
      },
    },
    updateLaterPolling: {
      id: 'update-later-polling',
      description:
        'A simple polling state that trigger another update request based on the configurable time interval.',
      after: {
        POLLING_INTERVAL: '#pending-update',
      },
    },
    restarting: {
      id: 'restarting',
      description:
        'A state in which the app attempts to restart itself. Will enter the "restarting failed" state to tell the user that the restart was unsuccessful and that a manual restart is necessary.',
      invoke: {
        id: 'restartApp',
        src: 'restartApp',
        onDone: {
          target: '#done',
        },
        onError: {
          target: '#restarting-failed',
          actions: 'onRestartAppError',
        },
      },
    },
    restartingFailed: {
      id: 'restarting-failed',
      description: 'A state that notifies the user that a manual restart is necessary.',
      type: 'final',
    },
    done: {
      id: 'done',
      description: 'A state in which the machine has completed all conceivable actions.',
      type: 'final',
    },
  },
});
